import { forwardRef, ReactNode, useId } from 'react';

interface Option {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  options?: Option[];
  placeholder?: string;
  children?: ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options = [], placeholder = 'Select...', className, children, ...rest }, ref) => {
    const generatedId = useId();
    const selectId = rest.id ?? generatedId;

    return (
      <div className="space-y-2">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-zinc-300">
            {label}
            {rest.required && <span className="text-red-400">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          {...rest}
          className={`w-full cursor-pointer appearance-none rounded-xl border bg-input px-4 py-3 transition-all
            ${
              error
                ? 'border-red-500/50 focus:ring-red-500/20'
                : 'border-border focus:border-blue-500/50 focus:ring-blue-500/20'
            }
            text-white focus:outline-none focus:ring-2
            disabled:cursor-not-allowed disabled:opacity-50
            bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM3MTcxN2IiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJtNiA5IDYgNiA2LTYiLz48L3N2Zz4=')]
            bg-size-[1rem] bg-position-[right_1rem_center] bg-no-repeat
            ${className || ''}`}
        >
          {placeholder && (
            <option value="" disabled className="bg-zinc-900 text-muted">
              {placeholder}
            </option>
          )}
          {children
            ? children
            : options.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-zinc-900">
                  {opt.label}
                </option>
              ))}
        </select>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
